// ═══════════════════════════════════════════════════════════════
// DEBRIS — Ticket Verification (public route, no auth)
// Monitor at disposal site scans ticket QR → lands here.
// Verify load %, confirm debris type, capture signatures.
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Paper, Title, Text, Group, Stack, Badge, Button,
  TextInput, Select, SimpleGrid, Divider, Loader,
  ThemeIcon,
} from '../../components/gab/GabComponents';
import { IconShieldCheck, IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { fetchTable, fetchRecordById, updateRecord, TABLES, FIELDS } from '../../lib/api-switch';
import { DEBRIS_TYPES, DEBRIS_TYPE_LABELS, TICKET_STATUS_LABELS, TICKET_STATUS_COLORS } from '../../types/debris';
import type { DebrisType, MeasurementMode, TicketStatus } from '../../types/debris';
import { primary } from '../../lib/colors';

// ── Signature pad (simple canvas) ─────────────────────────────
function SignaturePad({ label, onCapture }: { label: string; onCapture: (dataUrl: string) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [signed, setSigned] = useState(false);

  const getCtx = () => canvasRef.current?.getContext('2d') || null;

  const startDraw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const ctx = getCtx();
    if (!ctx) return;
    setDrawing(true);
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.beginPath();
    ctx.moveTo(x, y);
  }, []);

  const draw = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing) return;
    const ctx = getCtx();
    if (!ctx) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    const x = 'touches' in e ? e.touches[0].clientX - rect.left : e.nativeEvent.offsetX;
    const y = 'touches' in e ? e.touches[0].clientY - rect.top : e.nativeEvent.offsetY;
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#000';
    ctx.lineCap = 'round';
    ctx.lineTo(x, y);
    ctx.stroke();
  }, [drawing]);

  const endDraw = useCallback(() => {
    setDrawing(false);
    if (canvasRef.current) {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      onCapture(dataUrl);
      setSigned(true);
    }
  }, [onCapture]);

  const clear = () => {
    const ctx = getCtx();
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      setSigned(false);
      onCapture('');
    }
  };

  return (
    <div>
      <Group justify="space-between" mb={4}>
        <Text size="sm" fw={600}>{label}</Text>
        {signed && <Badge size="xs" color="green">Signed</Badge>}
      </Group>
      <canvas
        ref={canvasRef}
        width={400}
        height={160}
        style={{
          border: '2px solid #e5e7eb',
          borderRadius: 8,
          cursor: 'crosshair',
          touchAction: 'none',
          width: '100%',
          maxWidth: 400,
          background: '#fff',
        }}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
      />
      <Button size="compact-xs" variant="subtle" mt={4} onClick={clear}>Clear</Button>
    </div>
  );
}

// ── Field aliases ────────────────────────────────────────────
const FTK = FIELDS.debrisLoadTickets;
const FMN = FIELDS.debrisMonitors;
const FTR = FIELDS.debrisTrucks;

// Map raw V2 ticket record to a friendly object for display
function mapRawTicket(r: Record<string, unknown>): Record<string, unknown> {
  const rid = Number(r.record_id) || Number(r.id) || 0;
  return {
    id: rid,
    record_id: rid,
    ticketNumber: String(r[FTK.ticketNumber] ?? ''),
    truckId: String(r[FTK.truckId] ?? ''),
    debrisType: String(r[FTK.debrisType] ?? ''),
    measurementMode: String(r[FTK.measurementMode] ?? 'volumetric'),
    calculatedCubicYards: String(r[FTK.calculatedCubicYards] ?? ''),
    unitCount: String(r[FTK.unitCount] ?? ''),
    weight: String(r[FTK.weight] ?? ''),
    loadingLoadPct: String(r[FTK.loadingLoadPct] ?? ''),
    towerLoadPct: String(r[FTK.towerLoadPct] ?? ''),
    handLoaded: String(r[FTK.handLoaded] ?? ''),
    pickupStopsJson: String(r[FTK.pickupStopsJson] ?? '[]'),
    status: String(r[FTK.status] ?? 'draft'),
    loadingMonitorName: String(r[FTK.loadingMonitorName] ?? ''),
    loadingMonitorSignature: String(r[FTK.loadingMonitorSignature] ?? ''),
    driverPickupSignature: String(r[FTK.driverPickupSignature] ?? ''),
    monitorName: String(r[FTK.towerMonitorName] ?? ''),
    monitorSignature: String(r[FTK.towerMonitorSignature] ?? ''),
    driverSignature: String(r[FTK.driverDeliverySignature] ?? ''),
    verifiedAt: String(r[FTK.verifiedAt] ?? ''),
    notes: String(r[FTK.notes] ?? ''),
    // These come from truck record — populated after truck lookup
    truckNumber: '',
    cubicYardCapacity: '0',
    weightUnit: 'lbs',
    unitSubcategory: '',
  };
}

// ── Main Component ──────────────────────────────────────────
export default function TicketVerify() {
  const { ticketId } = useParams();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState<Record<string, unknown> | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Monitor list + truck data from Ignatius
  const [monitorOptions, setMonitorOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [truckHasTailgate, setTruckHasTailgate] = useState(true);

  // Verification form
  const [monitorName, setMonitorName] = useState('');
  const [loadPct, setLoadPct] = useState('');
  const [debrisType, setDebrisType] = useState('');
  const [unitCount, setUnitCount] = useState('');
  const [unitSubcategory, setUnitSubcategory] = useState('');
  const [weight, setWeight] = useState('');
  const [weightUnit, setWeightUnit] = useState('lbs');
  const [monitorSig, setMonitorSig] = useState('');
  const [driverSig, setDriverSig] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!ticketId) return;
    (async () => {
      try {
        // Fetch the specific ticket by ID + monitors + trucks in parallel
        const [rawTicket, monitorRows, truckRows] = await Promise.all([
          fetchRecordById(TABLES.debrisLoadTickets, ticketId),
          TABLES.debrisMonitors
            ? fetchTable(TABLES.debrisMonitors, { anonymous: true }).catch(() => [])
            : Promise.resolve([]),
          TABLES.debrisTrucks
            ? fetchTable(TABLES.debrisTrucks, { anonymous: true }).catch(() => [])
            : Promise.resolve([]),
        ]);

        if (rawTicket) {
          const mapped = mapRawTicket(rawTicket);
          // Look up truck to get capacity + tailgate status
          const truckId = mapped.truckId as string;
          if (truckId && (truckRows as any[]).length > 0) {
            const truck = (truckRows as any[]).find((t: any) =>
              String(t.record_id) === truckId || String(t.id) === truckId
            );
            if (truck) {
              if (String(truck[FTR.hasTailgate]) === 'false') setTruckHasTailgate(false);
              mapped.truckNumber = String(truck[FTR.truckNumber] ?? '');
              mapped.cubicYardCapacity = String(truck[FTR.capacityCY] ?? '0');
            }
          }
          setTicket(mapped);
          setDebrisType(mapped.debrisType as string);
          setLoadPct(mapped.towerLoadPct as string || mapped.loadingLoadPct as string || '');
          setUnitCount(mapped.unitCount as string);
          setWeight(mapped.weight as string);
          setMonitorName(mapped.monitorName as string);
        }
        // Build monitor dropdown options (active monitors only)
        const opts = (monitorRows as any[])
          .filter((m: any) => String(m[FMN.status] ?? 'active') === 'active')
          .map((m: any) => {
            const name = String(m[FMN.fullName] ?? '');
            const company = String(m[FMN.company] ?? '');
            return { value: name, label: company ? `${name} — ${company}` : name };
          });
        setMonitorOptions(opts);
      } catch (e) {
        console.error('TicketVerify fetch error:', e);
        setFetchError(e instanceof Error ? e.message : String(e));
      }
      setLoading(false);
    })();
  }, [ticketId]);

  const handleVerify = async () => {
    if (!ticket || !monitorName.trim()) return;
    if (!monitorSig) {
      alert('Monitor signature is required');
      return;
    }
    setSubmitting(true);
    try {
      const rid = Number(ticket.record_id || ticket.id);
      const FT = FIELDS.debrisLoadTickets;
      const cfg = DEBRIS_TYPES[debrisType as DebrisType];
      const mode = cfg?.measurementMode || 'volumetric';

      // Calculate CY if volumetric — apply FEMA reductions
      let calcCY = '';
      if (mode === 'volumetric' && loadPct) {
        let effectiveCap = parseFloat(String(ticket.cubicYardCapacity || '0'));
        // FEMA: no-tailgate trucks get 15% capacity reduction
        if (!truckHasTailgate) effectiveCap = effectiveCap * 0.85;
        // FEMA: hand-loaded = automatic 50% max load
        let pct = parseFloat(loadPct);
        const handLoaded = String(ticket.handLoaded) === 'true';
        if (handLoaded) pct = Math.min(pct, 50);
        calcCY = effectiveCap > 0 ? String(Math.round(effectiveCap * (pct / 100) * 100) / 100) : '';
      }

      // Decode existing meta from ticket notes so we can merge new meta fields
      const TMETA_RE = /\|\|\|TMETA\|\|\|([\s\S]*?)\|\|\|ENDTMETA\|\|\|/;
      const rawNotes = String(ticket.notes || '');
      let existingMeta: Record<string, string> = {};
      let userNotes = rawNotes.replace(TMETA_RE, '').trim();
      const metaMatch = rawNotes.match(TMETA_RE);
      if (metaMatch) { try { existingMeta = JSON.parse(metaMatch[1]); } catch {} }
      // Append monitor note to visible notes
      const monitorNote = notes ? `${userNotes ? `${userNotes} | ` : ''}Monitor: ${notes}` : userNotes;
      // unitSubcategory / weightUnit have no V2 columns — store in meta
      if (mode === 'unit') existingMeta.unitSubcategory = unitSubcategory;
      if (mode === 'weight') existingMeta.weightUnit = weightUnit;
      const nonEmptyMeta = Object.fromEntries(Object.entries(existingMeta).filter(([, v]) => v));
      const encodedNotes = Object.keys(nonEmptyMeta).length > 0
        ? `${monitorNote ? `${monitorNote}\n` : ''}|||TMETA|||${JSON.stringify(nonEmptyMeta)}|||ENDTMETA|||`
        : monitorNote;

      const fields: Array<[string, string]> = [
        [FT.status, 'verified'],
        [FT.towerMonitorName, monitorName.trim()],
        [FT.towerMonitorSignature, monitorSig],
        [FT.driverDeliverySignature, driverSig],
        [FT.debrisType, debrisType],
        [FT.verifiedAt, new Date().toISOString()],
        [FT.notes, encodedNotes],
      ];

      // Tower monitor's independent estimate (FEMA chain of custody)
      fields.push([FT.towerLoadPct, mode === 'volumetric' ? loadPct : '']);

      // Add measurement fields based on mode
      if (mode === 'volumetric') {
        // loadPercentage keeps the loading monitor's original estimate; tower's goes in towerLoadPct
        // calculatedCubicYards uses the tower's estimate (authoritative)
        fields.push([FT.calculatedCubicYards, calcCY]);
      } else if (mode === 'unit') {
        fields.push([FT.unitCount, unitCount]);
        // unitSubcategory has no V2 column — stored in meta (handled above)
      } else if (mode === 'weight') {
        fields.push([FT.weight, weight]);
        // weightUnit has no V2 column — stored in meta (handled above)
      }

      // Ignatius PUT max 10 fields — batch if needed
      if (fields.length <= 10) {
        await updateRecord(TABLES.debrisLoadTickets, rid, fields);
      } else {
        await updateRecord(TABLES.debrisLoadTickets, rid, fields.slice(0, 10));
        await updateRecord(TABLES.debrisLoadTickets, rid, fields.slice(10));
      }

      // Update local ticket state so UI shows verified
      setTicket(prev => prev ? { ...prev, status: 'verified', verifiedAt: new Date().toISOString(), monitorName: monitorName.trim() } : prev);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box mih="100vh" style={{ background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size="lg" />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box mih="100vh" style={{ background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Paper shadow="md" radius="md" p="xl" maw={500} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="red">
              <IconAlertCircle size={36} />
            </ThemeIcon>
            <Title order={3} ta="center">{fetchError ? 'Connection Error' : 'Ticket Not Found'}</Title>
            <Text ta="center" c="dimmed">
              {fetchError
                ? `Could not load ticket data: ${fetchError}. Check your network connection and try again.`
                : `Ticket #${ticketId} doesn't exist or the QR code is invalid.`}
            </Text>
            {fetchError && (
              <Button variant="light" onClick={() => window.location.reload()}>Retry</Button>
            )}
          </Stack>
        </Paper>
      </Box>
    );
  }

  const ticketNum = String(ticket.ticketNumber || `#${ticket.id}`);
  const status = String(ticket.status || 'draft') as TicketStatus;
  const cfg = DEBRIS_TYPES[debrisType as DebrisType];
  const mode: MeasurementMode = cfg?.measurementMode || 'volumetric';

  // Already verified — show read-only confirmation
  if (status === 'verified' || status === 'delivered') {
    const verifiedAt = ticket.verifiedAt ? new Date(String(ticket.verifiedAt)).toLocaleString() : 'Unknown';
    return (
      <Box mih="100vh" style={{ background: 'linear-gradient(135deg, var(--color-primary-900) 0%, var(--color-primary-700) 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Paper shadow="lg" radius="md" p="xl" maw={500} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="green">
              <IconShieldCheck size={36} />
            </ThemeIcon>
            <Title order={3} ta="center" c="#07963F">Ticket Already Verified</Title>
            <Text ta="center" c="dimmed">
              Ticket <strong>{ticketNum}</strong> was verified by <strong>{String(ticket.monitorName || 'Unknown')}</strong> on {verifiedAt}.
            </Text>
            <Paper p="sm" bg="gray.0" radius="md" withBorder w="100%">
              <SimpleGrid cols={2} spacing="xs">
                <div>
                  <Text size="xs" c="dimmed">Status</Text>
                  <Badge color={TICKET_STATUS_COLORS[status] || 'green'}>{TICKET_STATUS_LABELS[status] || status}</Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Debris Type</Text>
                  <Text size="sm" fw={600}>{DEBRIS_TYPE_LABELS[String(ticket.debrisType) as DebrisType] || String(ticket.debrisType || '-')}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Truck</Text>
                  <Text size="sm" fw={600}>{String(ticket.truckNumber || '-')}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Measurement</Text>
                  <Text size="sm" fw={600}>
                    {ticket.calculatedCubicYards ? `${ticket.calculatedCubicYards} CY` : ''}
                    {ticket.unitCount ? `${ticket.unitCount} units` : ''}
                    {ticket.weight ? `${ticket.weight} ${ticket.weightUnit || 'lbs'}` : ''}
                    {!ticket.calculatedCubicYards && !ticket.unitCount && !ticket.weight ? '-' : ''}
                  </Text>
                </div>
              </SimpleGrid>
            </Paper>
          </Stack>
        </Paper>
      </Box>
    );
  }

  // Voided — blocked
  if (status === 'voided') {
    return (
      <Box mih="100vh" style={{ background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <Paper shadow="md" radius="md" p="xl" maw={500} w="100%">
          <Stack align="center" gap="md">
            <ThemeIcon size={64} radius="xl" variant="light" color="red">
              <IconAlertCircle size={36} />
            </ThemeIcon>
            <Title order={3} ta="center">Ticket Voided</Title>
            <Text ta="center" c="dimmed">
              Ticket <strong>{ticketNum}</strong> has been voided and cannot be verified.
            </Text>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box mih="100vh" style={{ background: '#f5f5f5', padding: 24 }}>
      <Paper shadow="md" radius="md" p="xl" maw={700} mx="auto">
        <Stack align="center" gap="xs" mb="lg">
          <ThemeIcon size={48} radius="xl" variant="light" color="indigo">
            <IconShieldCheck size={24} />
          </ThemeIcon>
          <Title order={3} c={primary[900]}>Load Ticket Verification</Title>
          <Text size="sm" c="dimmed">Ticket: <strong>{ticketNum}</strong></Text>
        </Stack>

        {/* Disputed banner */}
        {status === 'disputed' && (
          <Paper p="sm" radius="md" mb="md" style={{ background: 'var(--mantine-color-orange-0)', border: '1px solid #f59e0b' }}>
            <Group gap="xs">
              <IconAlertCircle size={16} color="#92400e" />
              <Text size="sm" fw={600} c="#92400e">
                This ticket was disputed. Review and re-verify with corrected measurements.
              </Text>
            </Group>
            {String(ticket.notes || '') && (
              <Text size="xs" c="#92400e" mt={4}>{String(ticket.notes || '')}</Text>
            )}
          </Paper>
        )}

        {/* Ticket summary */}
        <Paper p="sm" bg="gray.0" radius="md" withBorder mb="md">
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
            <div>
              <Text size="xs" c="dimmed">Status</Text>
              <Badge color={TICKET_STATUS_COLORS[status] || 'gray'}>{TICKET_STATUS_LABELS[status] || status}</Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed">Truck</Text>
              <Text size="sm" fw={600}>{String(ticket.truckNumber || ticket.truckId || '-')}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Debris Type</Text>
              <Badge size="sm">{DEBRIS_TYPE_LABELS[String(ticket.debrisType) as DebrisType] || String(ticket.debrisType || '-')}</Badge>
            </div>
            <div>
              <Text size="xs" c="dimmed">Date</Text>
              <Text size="sm">{ticket.pickupTime ? new Date(String(ticket.pickupTime)).toLocaleString() : '-'}</Text>
            </div>
            {String(ticket.calculatedCubicYards || '') && (
              <div>
                <Text size="xs" c="dimmed">CY (from stops)</Text>
                <Text size="sm" fw={600}>{String(ticket.calculatedCubicYards)} CY</Text>
              </div>
            )}
            {String(ticket.unitCount || '') && (
              <div>
                <Text size="xs" c="dimmed">Unit Count</Text>
                <Text size="sm" fw={600}>{String(ticket.unitCount)} {DEBRIS_TYPES[String(ticket.debrisType) as DebrisType]?.unitLabel || 'units'}</Text>
              </div>
            )}
            {String(ticket.weight || '') && (
              <div>
                <Text size="xs" c="dimmed">Weight</Text>
                <Text size="sm" fw={600}>{String(ticket.weight)} {String(ticket.weightUnit || 'lbs')}</Text>
              </div>
            )}
            {String(ticket.handLoaded) === 'true' && (
              <div>
                <Text size="xs" c="dimmed">FEMA Flag</Text>
                <Badge size="sm" color="orange">Hand-Loaded (50% cap)</Badge>
              </div>
            )}
          </SimpleGrid>
        </Paper>

        {/* Pickup stops detail — so the monitor can see what's on the load */}
        {(() => {
          try {
            const stops = JSON.parse(String(ticket.pickupStopsJson || '[]'));
            if (!Array.isArray(stops) || stops.length === 0) return null;
            return (
              <Paper p="sm" radius="md" withBorder mb="md">
                <Text size="sm" fw={600} mb="xs">Pickup Stops ({stops.length})</Text>
                {stops.map((s: any, idx: number) => {
                  const sCfg = s.debrisType ? DEBRIS_TYPES[s.debrisType as DebrisType] : null;
                  return (
                    <Paper key={s.id || idx} p="xs" radius="sm" mb={4} style={{ background: 'var(--mantine-color-gray-0)', border: '1px solid var(--mantine-color-gray-2)' }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Group gap={6}>
                          <Badge size="xs" variant="light">Stop {idx + 1}</Badge>
                          {s.debrisType && <Badge size="xs" color="blue" variant="light">{DEBRIS_TYPE_LABELS[s.debrisType as DebrisType] || s.debrisType}</Badge>}
                          {s.unitCount && <Text size="xs" fw={600}>{s.unitCount} {sCfg?.unitLabel || 'CY'}</Text>}
                          {s.weight && <Text size="xs" fw={600}>{s.weight} {s.weightUnit || 'lbs'}</Text>}
                        </Group>
                        {s.lat && s.lng && <Text size="xs" c="dimmed">{parseFloat(s.lat).toFixed(4)}, {parseFloat(s.lng).toFixed(4)}</Text>}
                      </Group>
                      {s.address && <Text size="xs" c="dimmed" mt={2}>{s.address}</Text>}
                      {(s.beforePhotoData || s.afterPhotoData) && (
                        <Group gap="xs" mt={4}>
                          {s.beforePhotoData && (
                            <div>
                              <Text size="xs" c="dimmed">Before</Text>
                              <img src={s.beforePhotoData} alt="Before" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                            </div>
                          )}
                          {s.afterPhotoData && (
                            <div>
                              <Text size="xs" c="dimmed">After</Text>
                              <img src={s.afterPhotoData} alt="After" style={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 4 }} />
                            </div>
                          )}
                        </Group>
                      )}
                      {s.notes && <Text size="xs" mt={2}>{s.notes}</Text>}
                    </Paper>
                  );
                })}
              </Paper>
            );
          } catch { return null; }
        })()}

        {/* Loading monitor's pickup signatures if present */}
        {(String(ticket.loadingMonitorSignature || '') || String(ticket.driverPickupSignature || '')) && (
          <Paper p="sm" radius="md" withBorder mb="md" style={{ background: 'var(--mantine-color-blue-0)' }}>
            <Text size="sm" fw={600} mb="xs" c="blue">Pickup Signatures</Text>
            <Group gap="md">
              {String(ticket.loadingMonitorSignature || '') && (
                <div>
                  <Text size="xs" c="dimmed">Loading Monitor{String(ticket.loadingMonitorName || '') ? `: ${String(ticket.loadingMonitorName)}` : ''}</Text>
                  <img src={String(ticket.loadingMonitorSignature)} alt="Loading monitor" style={{ maxWidth: 150, maxHeight: 60, border: '1px solid #ddd', borderRadius: 4 }} />
                </div>
              )}
              {String(ticket.driverPickupSignature || '') && (
                <div>
                  <Text size="xs" c="dimmed">Driver (at pickup)</Text>
                  <img src={String(ticket.driverPickupSignature)} alt="Driver pickup" style={{ maxWidth: 150, maxHeight: 60, border: '1px solid #ddd', borderRadius: 4 }} />
                </div>
              )}
            </Group>
          </Paper>
        )}

        <Divider mb="md" label="Tower/Site Monitor Verification" labelPosition="center" />

        <Select label="Tower Monitor Name" required placeholder="Select monitor..."
          data={monitorOptions} value={monitorName || null}
          onChange={v => setMonitorName(v ?? '')}
          searchable clearable mb="sm" />

        {/* Show loading monitor info if present */}
        {(String(ticket.loadingMonitorName || '') || String(ticket.loadingLoadPct || '')) ? (
          <Paper p="xs" radius="sm" mb="sm" style={{ background: 'var(--mantine-color-blue-0)' }}>
            <Text size="xs" fw={600} c="blue">Loading Site Monitor Estimate</Text>
            <Group gap="md">
              {String(ticket.loadingMonitorName || '') && <Text size="sm">Monitor: {String(ticket.loadingMonitorName)}</Text>}
              {String(ticket.loadingLoadPct || '') && <Text size="sm">Load: {String(ticket.loadingLoadPct)}%</Text>}
            </Group>
          </Paper>
        ) : null}

        <Select label="Debris Type" data={Object.entries(DEBRIS_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
          value={debrisType || null} onChange={v => setDebrisType(v ?? '')} searchable mb="sm" />

        {/* Tower monitor's independent measurement */}
        {mode === 'volumetric' && (() => {
          const rawCap = parseFloat(String(ticket.cubicYardCapacity || '0'));
          const effectiveCap = truckHasTailgate ? rawCap : rawCap * 0.85;
          const pct = parseFloat(loadPct || '0');
          const handLoaded = String(ticket.handLoaded) === 'true';
          const effectivePct = handLoaded ? Math.min(pct, 50) : pct;
          const cy = effectiveCap > 0 && effectivePct > 0 ? (effectiveCap * effectivePct / 100).toFixed(1) : '-';
          const loadingPct = parseFloat(String(ticket.loadingLoadPct || '0'));
          const discrepancy = loadingPct > 0 && pct > 0 ? Math.abs(loadingPct - pct) : 0;
          const reductions: string[] = [];
          if (handLoaded) reductions.push('50% hand-load cap');
          if (!truckHasTailgate) reductions.push('15% no-tailgate reduction');
          return (
            <>
              <SimpleGrid cols={2} spacing="sm" mb="xs">
                <TextInput label="Tower Monitor Load % (your independent estimate)" value={loadPct}
                  onChange={e => setLoadPct(e.target.value)} placeholder="Visual estimate %" />
                <TextInput label="Calculated CY" readOnly
                  value={cy !== '-' ? `${cy} CY` : '-'}
                  description={reductions.length > 0 ? reductions.join(', ') : `${effectiveCap.toFixed(1)} CY capacity × ${effectivePct}%`} />
              </SimpleGrid>
              {discrepancy > 15 && (
                <Paper p="xs" radius="sm" mb="sm" style={{ background: 'var(--mantine-color-red-0)', border: '1px solid var(--mantine-color-red-4)' }}>
                  <Text size="xs" fw={600} c="red">
                    Discrepancy: Loading monitor estimated {loadingPct}%, you estimated {pct}% — {discrepancy.toFixed(0)} point difference. Please verify and document in notes.
                  </Text>
                </Paper>
              )}
            </>
          );
        })()}
        {mode === 'unit' && (
          <SimpleGrid cols={cfg?.subcategories ? 2 : 1} spacing="sm" mb="sm">
            <TextInput label={`Quantity (${cfg?.unitLabel || 'units'})`} type="number"
              value={unitCount} onChange={e => setUnitCount(e.target.value)} />
            {cfg?.subcategories && (
              <Select label="Subcategory" data={cfg.subcategories.map(s => ({ value: s, label: s }))}
                value={unitSubcategory || null} onChange={v => setUnitSubcategory(v ?? '')} />
            )}
          </SimpleGrid>
        )}
        {mode === 'weight' && (
          <SimpleGrid cols={2} spacing="sm" mb="sm">
            <TextInput label="Weight" type="number" value={weight}
              onChange={e => setWeight(e.target.value)} />
            <Select label="Unit" data={[{ value: 'lbs', label: 'Pounds' }, { value: 'tons', label: 'Tons' }]}
              value={weightUnit} onChange={v => setWeightUnit(v ?? 'lbs')} />
          </SimpleGrid>
        )}

        <Divider mb="md" label="Signatures" labelPosition="center" />

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
          <SignaturePad label="Monitor Signature" onCapture={setMonitorSig} />
          <SignaturePad label="Driver Signature" onCapture={setDriverSig} />
        </SimpleGrid>

        <TextInput label="Notes (optional)" value={notes}
          onChange={e => setNotes(e.target.value)} placeholder="Any discrepancies or observations" mb="lg" />

        <Button fullWidth size="lg" color="green" leftSection={<IconCheck size={18} />}
          onClick={handleVerify} loading={submitting}
          disabled={!monitorName.trim() || !monitorSig}>
          Verify Load Ticket
        </Button>

        <Text size="xs" c="dimmed" ta="center" mt="md">
          OpenGov Emergency Management Console — Debris ADMS
        </Text>
      </Paper>
    </Box>
  );
}
